import React from 'react'
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Chip,
  Alert,
  Typography,
} from '@mui/material'

/**
 * Functions Section Component
 * Configure therapist roles: screening, therapy, consultant
 */
export default function FunctionsSection({ data, onChange, disabled, loading, error }) {
  const handleToggle = (role, field, value) => {
    onChange({
      ...data,
      [role]: {
        ...data[role],
        [field]: value,
      },
    })
  }

  const handleModulesChange = (role, modules) => {
    onChange({
      ...data,
      [role]: {
        ...data[role],
        modules: modules,
      },
    })
  }

  const handleTypesChange = (role, types) => {
    onChange({
      ...data,
      [role]: {
        ...data[role],
        types: types,
      },
    })
  }

  // Mock modules and types (replace with real data from API if available)
  const screeningModules = ['pediatric', 'adult', 'neurodevelopmental', 'personality']
  const therapyTypes = ['cbt', 'dbt', 'family-counseling', 'addiction', 'child-therapy']

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Clinical Functions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enable roles and select applicable modules/types
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 3 }}>
        {/* Screening */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={data.screening?.enabled || false}
                onChange={(e) => handleToggle('screening', 'enabled', e.target.checked)}
                disabled={disabled || loading}
              />
            }
            label="Enable as Screening Psychologist"
          />

          {data.screening?.enabled && (
            <Box sx={{ mt: 2, ml: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Screening Modules:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {screeningModules.map((module) => (
                  <Chip
                    key={module}
                    label={module.charAt(0).toUpperCase() + module.slice(1)}
                    onClick={() => {
                      const current = data.screening?.modules || []
                      const updated = current.includes(module)
                        ? current.filter((m) => m !== module)
                        : [...current, module]
                      handleModulesChange('screening', updated)
                    }}
                    color={data.screening?.modules?.includes(module) ? 'primary' : 'default'}
                    disabled={disabled || loading}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Therapy */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={data.therapy?.enabled || false}
                onChange={(e) => handleToggle('therapy', 'enabled', e.target.checked)}
                disabled={disabled || loading}
              />
            }
            label="Enable as Therapist"
          />

          {data.therapy?.enabled && (
            <Box sx={{ mt: 2, ml: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Therapy Types:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {therapyTypes.map((type) => (
                  <Chip
                    key={type}
                    label={type.toUpperCase()}
                    onClick={() => {
                      const current = data.therapy?.types || []
                      const updated = current.includes(type)
                        ? current.filter((t) => t !== type)
                        : [...current, type]
                      handleTypesChange('therapy', updated)
                    }}
                    color={data.therapy?.types?.includes(type) ? 'primary' : 'default'}
                    disabled={disabled || loading}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Consultant */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={data.consultant?.enabled || false}
                onChange={(e) => handleToggle('consultant', 'enabled', e.target.checked)}
                disabled={disabled || loading}
              />
            }
            label="Enable as Consultant / Mentor"
          />

          {data.consultant?.enabled && (
            <Box sx={{ mt: 2, ml: 4 }}>
              <TextField
                label="Consultation Fee Slab"
                placeholder="e.g., ₹1000-1500 / 30-45 mins"
                value={data.consultant?.feeSlab || ''}
                onChange={(e) => handleToggle('consultant', 'feeSlab', e.target.value)}
                disabled={disabled || loading}
                fullWidth
                size="small"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
